import React, { Component, ErrorInfo, ReactNode } from 'react';
import Router from 'next/router';
import styled from 'styled-components';

import { isPostHogLoaded, posthog } from '@lib/posthog.lib';
import { reloadWindow } from '@lib/utils/reloadWindow';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  reported: boolean;
}

/**
 * Global render-error boundary that reports sanitized exceptions to PostHog.
 * "Try Again" always performs a full page reload to avoid React error loops.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    reported: false,
  };

  public static getDerivedStateFromError(_: Error): Partial<State> {
    return { hasError: true };
  }

  public componentDidMount() {
    Router.events.on('routeChangeComplete', this.handleRouteChange);
  }

  public componentWillUnmount() {
    Router.events.off('routeChangeComplete', this.handleRouteChange);
  }

  public componentDidCatch(error: Error, _errorInfo: ErrorInfo) {
    if (!isPostHogLoaded()) {
      return;
    }

    const sanitizedError = new Error('UI render failed');

    sanitizedError.name = error.name || 'Error';
    sanitizedError.stack = undefined;

    posthog.captureException(sanitizedError, {
      boundary: 'ErrorBoundary',
    });
    this.setState({ reported: true });
  }

  private readonly handleRouteChange = () => {
    this.setState((state) =>
      state.hasError ? { hasError: false, reported: false } : null
    );
  };

  private readonly handleReset = () => {
    reloadWindow();
  };

  private renderFallback(): ReactNode {
    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <Container role='alert'>
        <Heading>
          {this.state.reported
            ? 'An unexpected error occurred. Our technical team has been notified.'
            : 'An unexpected error occurred. Please try again.'}
        </Heading>
        <RetryButton type='button' onClick={this.handleReset}>
          Try Again
        </RetryButton>
      </Container>
    );
  }

  public render() {
    if (this.state.hasError) {
      return this.renderFallback();
    }

    return this.props.children;
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
`;

const Heading = styled.h1`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
`;

const RetryButton = styled.button`
  cursor: pointer;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  background: #1677ff;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
`;

export default ErrorBoundary;
